import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'

import BadgeIsValid from '~/components/BadgeIsValid'
import Table from '~/components/Table'
import { Badge } from '~/components/UI/Badge'
import { FileInput, InputHidden, Label } from '~/components/unform'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import FileToUpload from '../FileToUpload'
import { schema } from './schema'

import * as S from './styles'

export const AdditionalAssetsFormContent = ({
  assetSettings,
  handleAssetsReady,
  parentIndex,
  initialAssets
}: AssetAddFormContentProps) => {
  const [assetsReady, setAssetsReady] = useState<AdditionalAssetsToTable[]>([])

  const { t } = useAPPTranslation()

  /**
   * Lógica para a funcionalidade principal
   */

  const [filesToUpload, setFilesToUpload] = useState<FilesToUpload>([])

  const handleDeleteAsset = useCallback(
    (data: DataAssetHandle) => {
      setAssetsReady(state => state.filter(item => item.id !== data.id))
    },
    [setAssetsReady]
  )

  // Carrega dados iniciais quando existirem (modo edição)
  useEffect(() => {
    if (initialAssets?.length && assetsReady.length === 0) {
      const formattedAssets: AdditionalAssetsToTable[] = initialAssets.map(
        asset => ({
          id: asset.url,
          media_url: asset.url,
          fileMedia: new File([], asset.url),
          dimensions: {
            width: Number(asset.width),
            height: Number(asset.height)
          },
          handleDeleteAsset
        })
      )
      setAssetsReady(formattedAssets)
    }
  }, [initialAssets, handleDeleteAsset, assetsReady.length])

  const handleSaveAsset = useCallback(
    (data: ItemAdditionalAssetsTable) => {
      setFilesToUpload(prevState =>
        prevState.filter(item => item.id !== data.id)
      )

      const setter = (prevState: AdditionalAssetsToTable[]) => {
        if (assetSettings.quantity > 1) {
          return [...prevState, { ...data, handleDeleteAsset }]
        }

        return [{ ...data, handleDeleteAsset }]
      }

      setAssetsReady(setter)
    },
    [assetSettings.quantity, handleDeleteAsset]
  )

  /**
   * Handle multiple upload
   */

  const handlePrepareToUploadImage = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const limit = assetSettings.quantity - assetsReady.length

      if (limit === 0) return

      const fileList = Array.from(
        event.target.files as FileList,
        (item, index) => ({ file: item, id: `${item.name}_${index}` })
      )

      setFilesToUpload(fileList.slice(0, limit))
    },
    [assetSettings.quantity, assetsReady.length]
  )

  const handleDeleteFileBeforeUpload = (removeId: string) => {
    setFilesToUpload(prevState =>
      prevState.filter(item => item.id !== removeId)
    )
  }

  /**
   * Placements
   */

  const options = useMemo(() => {
    if (assetSettings) {
      const arrayValue: OptionsInterface[] = [assetSettings].map(item => ({
        value: item.name,
        label: item.size,
        width: item.width,
        height: item.height
      }))

      arrayValue.sort((a, b) =>
        a.width !== b.width ? a.width - b.width : a.height - b.height
      )

      return arrayValue
    }
  }, [assetSettings])

  const getPlacementByDimension = useCallback(
    ({ width, height }: Dimension) => {
      const findPlacement = [assetSettings]?.find(
        item => Number(item.width) === width && Number(item.height) === height
      )

      return findPlacement
    },
    []
  )

  const limitExceeded = useMemo(
    () => assetsReady.length >= assetSettings.quantity,
    [assetSettings.quantity, assetsReady.length]
  )

  useEffect(() => {
    handleAssetsReady(prevState => ({
      ...prevState,
      [assetSettings.name]: limitExceeded
    }))
  }, [assetSettings.name, handleAssetsReady, limitExceeded])

  return (
    <>
      <S.WrapperViewAddBanner>
        <Label
          icon={<BadgeIsValid isValid={limitExceeded} />}
          text={assetSettings.title}
          helperText={`Quantidade permitida: ${assetSettings.quantity}`}
          htmlFor={`mediaFile_${assetSettings.name}`}
        />
      </S.WrapperViewAddBanner>

      {!limitExceeded && (
        <section className="multipleUploadWrapper">
          <FileInput
            name={`mediaFile_${assetSettings.name}`}
            accept={'image/*'}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              handlePrepareToUploadImage(event)
            }
            isMultiple={assetSettings.quantity > 1}
            hideIcons
            disabled={limitExceeded}
          >
            <div className="pt-3 mt-3 border-top border-tertiary">
              <h4>{t('rm:allowedMediaSizes_one')}</h4>

              <div className="d-flex gap 2 justify-content-center flex-wrap">
                {options?.map(item => (
                  <div key={item.value}>
                    <Badge template="primary">{item.label}</Badge>
                  </div>
                ))}
              </div>

              {assetSettings.quantity > 1 && (
                <small className="text-muted d-block">
                  Até {assetSettings.quantity} mídias permitidas
                </small>
              )}

              {assetSettings.quantity === 1 && (
                <span className="text-muted d-block">
                  Apenas uma mídia permitida
                </span>
              )}
            </div>
          </FileInput>

          <S.UploadQueue>
            {filesToUpload?.map(item => (
              <FileToUpload
                data={item}
                key={item.id}
                getPlacementByDimension={getPlacementByDimension}
                onDelete={() => handleDeleteFileBeforeUpload(item.id)}
                onSave={handleSaveAsset}
              />
            ))}
          </S.UploadQueue>
        </section>
      )}

      {!!assetsReady.length &&
        assetsReady.map((item, index) => (
          <>
            {/**
             * Usando input hidden para facilitar a manipulação do payload
             * pegando os dados diretamente do form data
             */}
            <InputHidden
              name={`additional_assets[${parentIndex}][${index}].url`}
              value={item.media_url}
            />

            <InputHidden
              name={`additional_assets[${parentIndex}][${index}].width`}
              value={item.dimensions.width}
            />

            <InputHidden
              name={`additional_assets[${parentIndex}][${index}].height`}
              value={item.dimensions.height}
            />

            <InputHidden
              name={`additional_assets[${parentIndex}][${index}].name`}
              value={assetSettings.name}
            />
          </>
        ))}

      {!!assetsReady.length && (
        <S.WrapperTabler>
          <Table schema={schema} data={assetsReady} />
        </S.WrapperTabler>
      )}
    </>
  )
}
